import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, BellIcon, CircleAlertIcon, MapPinIcon, SearchIcon, StarIcon } from 'lucide-react';
import { demoCourses, demoEvents, demoListings, demoTasks, landingBImages } from '../../data/landingBVisual';

/**
 * The four product stripes.
 *
 * Each is a deep-green editorial panel with one real interface fragment laid
 * over its photography. The fragments are built from the same tokens, badges
 * and rows as the signed-in product — not marketing rectangles — so what a
 * visitor sees here is what they get after signing in.
 */
interface StripeProps {
  index: string;
  eyebrow: string;
  title: React.ReactNode;
  copy: string;
  cta: string;
  to: string;
  children: React.ReactNode;
}
function Stripe({
  index,
  eyebrow,
  title,
  copy,
  cta,
  to,
  children
}: StripeProps) {
  return <section className="overflow-hidden rounded-panel bg-brand-900" data-id="element-2716" data-mp="2kw7z7nujdn9w">
      <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)]" data-id="element-2717" data-mp="2kxiz3ckv2avq">
        <div className="flex flex-col justify-center gap-4 p-7 sm:p-9" data-id="element-2718" data-mp="1be93eicybp8f">
          <p className="text-caption font-semibold uppercase tracking-[0.16em] text-brand-200" data-id="element-2719" data-mp="17cntn5vprr2g">
            <span className="cz-num mr-2 text-white/50" data-id="element-2720" data-mp="1lc6s16yq3o1k">{index}</span>
            {eyebrow}
          </p>
          <h2 className="text-h2 font-bold leading-[1.15] tracking-tight text-white" data-id="element-2721" data-mp="1gcav1rog0wvi">{title}</h2>
          <p className="max-w-[38ch] text-body leading-relaxed text-white/70" data-id="element-2722" data-mp="1ubk1w0qreemt">{copy}</p>
          <Link to={to} className="group mt-1 inline-flex min-h-touch w-fit items-center gap-2 text-body font-semibold text-white" data-id="element-2723" data-mp="1qb9v4lv57k6w">
            {cta}
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2724" data-mp="1z325kwk62tbh" />
          </Link>
        </div>
        <div className="relative min-h-[18rem]" data-id="element-2725" data-mp="291hucsvq1qht">{children}</div>
      </div>
    </section>;
}
export function ProductStripes() {
  return <div className="mx-auto max-w-[88rem] space-y-5 px-4 py-14 sm:px-8 lg:py-16" data-id="element-2726" data-mp="2g5r23l4k213z">
      {/* ------------------------------------------------------- academics */}
      <Stripe index="01" eyebrow="Academics" title={<>
            Stay ahead of
            <br data-id="element-2728" data-mp="2utlzhetictne" />
            your semester.
          </>} copy="Manage courses, timetable, results and assignments with ease." cta="Explore Academics" to="/academics" data-id="element-2727" data-mp="2q2vqzspscaoh">
        <img src={landingBImages.academics} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-25" data-id="element-2729" data-mp="33sz8y7hmti84" />
        <div className="cz-preview relative grid h-full gap-3 p-5 sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" data-id="element-2730" data-mp="3dqvjax2qw956">
          <div className="overflow-hidden rounded-xl border border-line bg-surface" data-id="element-2731" data-mp="1egx260cdg5po">
            <div className="flex items-center justify-between border-b border-line px-3 py-2" data-id="element-2732" data-mp="1jeynyt16mj4u">
              <span className="text-[13px] font-bold text-ink" data-id="element-2733" data-mp="1bvfutty859ij">Academics</span>
              <span className="flex items-center gap-2 text-ink-subtle" data-id="element-2734" data-mp="173o15ajruno6">
                <SearchIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2735" data-mp="124l8yqow4bxm" />
                <BellIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2736" data-mp="1254f7a56hbvt" />
              </span>
            </div>
            <div className="p-3" data-id="element-2737" data-mp="x7kz04ox6p18">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2738" data-mp="1ut8m1yzhjv03">
                My courses
              </p>
              <ul className="mt-2 space-y-2" data-id="element-2739" data-mp="1pvqmv0b9su5a">
                {demoCourses.map(course => <li key={course.code} className="rounded-lg border border-line bg-canvas p-2.5" data-id="element-2740" data-mp="1ku193cwny2di">
                    <div className="flex items-baseline justify-between gap-2" data-id="element-2741" data-mp="1fvgafhx27qr6">
                      <span className="truncate text-[13px] font-semibold text-ink" data-id="element-2742" data-mp="9gflhw6gc8u3">
                        {course.title}
                      </span>
                      <span className="cz-num shrink-0 text-[11px] font-semibold text-brand-800" data-id="element-2743" data-mp="4jfglolc9c4k">
                        {course.progress}%
                      </span>
                    </div>
                    <p className="cz-num mt-0.5 text-[11px] text-ink-muted" data-id="element-2744" data-mp="1y43714j37b7o">{course.code}</p>
                    <div role="progressbar" aria-valuenow={course.progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${course.title} progress`} className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line" data-id="element-2745" data-mp="1jaznirlxqneb">
                      <span className="block h-full rounded-full bg-brand-700" style={{
                    width: `${course.progress}%`
                  }} data-id="element-2746" data-mp="222sjmuwawoq9" />
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-line bg-surface p-3" data-id="element-2747" data-mp="vp2wx4jhq6i6">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2748" data-mp="39tzl8cnwuije">
              Upcoming
            </p>
            <ul className="mt-2 space-y-2" data-id="element-2749" data-mp="34yk5tga98ekv">
              {demoTasks.map(task => <li key={task.title} className="rounded-lg border border-line px-2.5 py-2" data-id="element-2750" data-mp="3ituycn110nm2">
                  <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink" data-id="element-2751" data-mp="2r49lrc97d8l5">
                    {task.urgent && <CircleAlertIcon aria-hidden className="h-3.5 w-3.5 text-danger-700" data-id="element-2752" data-mp="2m79kdenasu0d" />}
                    {task.title}
                  </p>
                  <p className="truncate text-[11px] text-ink-muted" data-id="element-2753" data-mp="2v64n2gr4vlyx">{task.detail}</p>
                  <p className={`mt-1 text-[11px] font-semibold ${task.urgent ? 'text-danger-700' : 'text-ink-subtle'}`} data-id="element-2754" data-mp="27gack5uae9hu">
                    {task.due}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>
      </Stripe>

      {/* ----------------------------------------------------- marketplace */}
      <Stripe index="02" eyebrow="Marketplace" title={<>
            Your campus
            <br data-id="element-2756" data-mp="l2p4dwysxp37" />
            has its own economy.
          </>} copy="Buy, sell, discover and earn around campus." cta="Explore Marketplace" to="/market" data-id="element-2755" data-mp="g5oxxky1ikbk">
        <img src={landingBImages.marketplace} alt="Students buying and selling at a campus market" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-2757" data-mp="3okfrg2y66dhz" />
        <div aria-hidden className="absolute inset-0 bg-brand-900/25" data-id="element-2758" data-mp="18tbb9kx2l3bm" />
        <div className="cz-preview relative flex h-full items-center justify-end p-5" data-id="element-2759" data-mp="18ve1o624wygv">
          <div className="w-full max-w-xs overflow-hidden rounded-xl border border-line bg-surface shadow-[0_20px_44px_rgba(0,0,0,0.3)]" data-id="element-2760" data-mp="1du8v01xqa5f8">
            <div className="flex items-center justify-between border-b border-line px-3 py-2" data-id="element-2761" data-mp="2tak98g7u4qhv">
              <span className="text-[13px] font-bold text-ink" data-id="element-2762" data-mp="2ok3son8pwora">Popular near you</span>
              <SearchIcon aria-hidden className="h-3.5 w-3.5 text-ink-subtle" data-id="element-2763" data-mp="2jlafkvxcezrn" />
            </div>
            <ul className="divide-y divide-line" data-id="element-2764" data-mp="2em7lg6iwidyg">
              {demoListings.map(listing => <li key={listing.title} className="flex items-center gap-2.5 px-3 py-2.5" data-id="element-2765" data-mp="2akm3i6rif9he">
                  <span className="min-w-0 flex-1" data-id="element-2766" data-mp="2amysh3voqk3y">
                    <span className="block truncate text-[13px] font-semibold text-ink" data-id="element-2767" data-mp="25mashsphasxl">
                      {listing.title}
                    </span>
                    <span className="cz-num block truncate text-[11px] text-ink-muted" data-id="element-2768" data-mp="3u4o8nqvsxtf">
                      {listing.price} · {listing.meta}
                    </span>
                  </span>
                  <span className="cz-num inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-ink" data-id="element-2769" data-mp="33v3jeg6r2vnp">
                    <StarIcon aria-hidden className="h-3 w-3 fill-warn-700 text-warn-700" data-id="element-2770" data-mp="38upx0qqhqdx1" />
                    {listing.rating}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </Stripe>

      {/* --------------------------------------------------------- housing */}
      <Stripe index="03" eyebrow="Housing" title={<>
            Find a place
            <br data-id="element-2772" data-mp="2q50305ujxw66" />
            that feels right.
          </>} copy="Inspected accommodation close to campus. Safe, trusted, convenient." cta="Explore Housing" to="/housing" data-id="element-2771" data-mp="2l3sug4kl4syg">
        <img src={landingBImages.housing} alt="A modern student residence near campus" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-2773" data-mp="2g7lnc8ggdbex" />
        <div aria-hidden className="absolute inset-0 bg-brand-900/20" data-id="element-2774" data-mp="1yvx4656szug2" />
        <div className="cz-preview relative flex h-full items-center p-5" data-id="element-2775" data-mp="1ytu7j2b5b32d">
          <div className="w-full max-w-sm overflow-hidden rounded-xl border border-line bg-surface shadow-[0_20px_44px_rgba(0,0,0,0.3)]" data-id="element-2776" data-mp="1tyok1z23hgc6">
            <p className="border-b border-line px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2777" data-mp="23v9pumlxuvh2">
              Recommended for you
            </p>
            <div className="flex gap-3 p-3" data-id="element-2778" data-mp="2horaybu9wg97">
              <img src={landingBImages.housing} alt="" loading="lazy" className="h-20 w-24 shrink-0 rounded-lg object-cover" data-id="element-2779" data-mp="2dluo28xp9xzg" />
              <div className="min-w-0" data-id="element-2780" data-mp="2mlhrt1dwjfvo">
                <p className="truncate text-[13px] font-bold text-ink" data-id="element-2781" data-mp="2mmkjvcmynod6">Cedarwood Residence</p>
                <p className="cz-num mt-0.5 text-[13px] font-semibold text-brand-800" data-id="element-2782" data-mp="x735enqcw53c">
                  ₦450,000 / year
                </p>
                <p className="cz-num mt-0.5 flex items-center gap-1 text-[11px] text-ink-muted" data-id="element-2783" data-mp="3n6kmog67vzey">
                  <MapPinIcon aria-hidden className="h-3 w-3" data-id="element-2784" data-mp="3i80uvz4xzteo" />
                  0.8 km from UNILAG
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-id="element-2785" data-mp="3d7wgqfribarm">
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-800" data-id="element-2786" data-mp="397cj7b30u54y">
                    <BadgeCheckIcon aria-hidden className="h-3 w-3" data-id="element-2787" data-mp="9rfw1b60ygpa" />
                    Inspected
                  </span>
                  <span className="cz-num inline-flex items-center gap-1 text-[10px] font-semibold text-ink" data-id="element-2788" data-mp="4uxrabap2icy">
                    <StarIcon aria-hidden className="h-3 w-3 fill-warn-700 text-warn-700" data-id="element-2789" data-mp="3s5x60qxvqocs" />
                    4.7 (30)
                  </span>
                </div>
              </div>
            </div>
            <p className="border-t border-line px-3 py-2 text-[10px] text-ink-subtle" data-id="element-2790" data-mp="nrqnafb8xxaz">
              Inspected means a representative visited and confirmed the property.
            </p>
          </div>
        </div>
      </Stripe>

      {/* ---------------------------------------------------------- campus */}
      <Stripe index="04" eyebrow="Campus" title={<>
            There&rsquo;s always
            <br data-id="element-2792" data-mp="2kei0i6s4kzkl" />
            something happening.
          </>} copy="Events, societies, sports and opportunities on your campus." cta="Explore Campus" to="/community" data-id="element-2791" data-mp="2ffex0qrsn1eq">
        <img src={landingBImages.events} alt="Students at an outdoor campus event" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-2793" data-mp="2ube3xy362via" />
        <div aria-hidden className="absolute inset-0 bg-brand-900/30" data-id="element-2794" data-mp="333y8ff653ub1" />
        <div className="cz-preview relative flex h-full items-center p-5" data-id="element-2795" data-mp="3855cb355w9cf">
          <div className="w-full overflow-hidden rounded-xl border border-line bg-surface shadow-[0_20px_44px_rgba(0,0,0,0.3)]" data-id="element-2796" data-mp="3d2dtp1ihk9cm">
            <p className="border-b border-line px-3 py-2 text-[13px] font-bold text-ink" data-id="element-2797" data-mp="1d17c5q8iuhbp">
              Upcoming events
            </p>
            <ul className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-2798" data-mp="17zqctz5or2x8">
              {demoEvents.map((event, index) => <li key={event.title} className="overflow-hidden rounded-lg border border-line" data-id="element-2799" data-mp="1hz8ta5me06kj" data-mpi={index}>
                  <img src={[landingBImages.events, landingBImages.campusBuilding, landingBImages.meetCampus, landingBImages.connect][index]} alt="" loading="lazy" className="h-16 w-full object-cover" data-id="element-2800" data-mp="1vqmave0qiucx" />
                  <div className="p-2" data-id="element-2801" data-mp="bd58zjlo8pp9">
                    <p className="truncate text-[11px] font-semibold text-ink" data-id="element-2802" data-mp="6d0qjo17dkwe">{event.title}</p>
                    <p className="cz-num truncate text-[10px] text-ink-muted" data-id="element-2803" data-mp="2hzay5mnob409">{event.date}</p>
                    <p className="truncate text-[10px] text-ink-subtle" data-id="element-2804" data-mp="2mz70zikmwpe8">{event.venue}</p>
                  </div>
                </li>)}
            </ul>
            <p className="border-t border-line px-3 py-2 text-[10px] text-ink-subtle" data-id="element-2805" data-mp="287x4hviekyf1">
              Demonstration events. Real listings are published by campus organisers.
            </p>
          </div>
        </div>
      </Stripe>
    </div>;
}